/**
 * Performance Monitor Service
 *
 * Tracks search performance metrics to measure optimization improvements.
 * Part of Phase 1 Quick Wins implementation.
 */

interface SearchMetric {
  timestamp: number;
  searchType: 'initial' | 'search' | 'filter' | 'pagination';
  query?: string;
  layer?: string;
  category?: string;
  subcategory?: string;
  resultCount: number;
  responseTime: number;
  apiCalls: number;
  dataTransferred: number; // bytes
  cacheHits: number;
  cacheMisses: number;
  clientSideFiltering: boolean;
  serverSideFiltering: boolean;
}

interface PerformanceStats {
  totalSearches: number;
  averageResponseTime: number;
  totalApiCalls: number;
  totalDataTransferred: number;
  cacheHitRate: number;
  clientSideFilteringCount: number;
  serverSideFilteringCount: number;
  lastUpdated: number;
}

export class PerformanceMonitorService {
  private static readonly STORAGE_KEY = 'nna_performance_metrics';
  private static readonly MAX_METRICS = 1000; // Keep only last 1000 metrics
  private static readonly STATS_KEY = 'nna_performance_stats';

  /**
   * Record a search performance metric
   */
  static recordSearch(metric: Omit<SearchMetric, 'timestamp'>): void {
    try {
      const fullMetric: SearchMetric = {
        ...metric,
        timestamp: Date.now(),
      };

      // Get existing metrics
      const metrics = this.getMetrics();
      metrics.push(fullMetric);

      // Keep only last MAX_METRICS entries
      if (metrics.length > this.MAX_METRICS) {
        metrics.splice(0, metrics.length - this.MAX_METRICS);
      }

      // Store updated metrics
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(metrics));

      // Update aggregated stats
      this.updateStats(fullMetric);

      // Log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 [PERFORMANCE] Search metric recorded:', {
          type: metric.searchType,
          responseTime: `${metric.responseTime}ms`,
          resultCount: metric.resultCount,
          apiCalls: metric.apiCalls,
          clientSideFiltering: metric.clientSideFiltering,
          serverSideFiltering: metric.serverSideFiltering,
        });
      }
    } catch (error) {
      console.warn('Failed to record performance metric:', error);
    }
  }

  /**
   * Get all stored metrics
   */
  static getMetrics(): SearchMetric[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to read performance metrics:', error);
      return [];
    }
  }

  /**
   * Get aggregated performance statistics
   */
  static getStats(): PerformanceStats | null {
    try {
      const stored = localStorage.getItem(this.STATS_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('Failed to read performance stats:', error);
      return null;
    }
  }

  /**
   * Update aggregated statistics
   */
  private static updateStats(metric: SearchMetric): void {
    try {
      let stats = this.getStats() || {
        totalSearches: 0,
        averageResponseTime: 0,
        totalApiCalls: 0,
        totalDataTransferred: 0,
        cacheHitRate: 0,
        clientSideFilteringCount: 0,
        serverSideFilteringCount: 0,
        lastUpdated: Date.now(),
      };

      // Update counters
      stats.totalSearches++;
      stats.totalApiCalls += metric.apiCalls;
      stats.totalDataTransferred += metric.dataTransferred;

      if (metric.clientSideFiltering) {
        stats.clientSideFilteringCount++;
      }

      if (metric.serverSideFiltering) {
        stats.serverSideFilteringCount++;
      }

      // Calculate new average response time
      const oldTotal = stats.averageResponseTime * (stats.totalSearches - 1);
      stats.averageResponseTime =
        (oldTotal + metric.responseTime) / stats.totalSearches;

      // Calculate cache hit rate
      const totalCacheRequests = metric.cacheHits + metric.cacheMisses;
      if (totalCacheRequests > 0) {
        stats.cacheHitRate = metric.cacheHits / totalCacheRequests;
      }

      stats.lastUpdated = Date.now();

      // Store updated stats
      localStorage.setItem(this.STATS_KEY, JSON.stringify(stats));
    } catch (error) {
      console.warn('Failed to update performance stats:', error);
    }
  }

  /**
   * Get performance improvement comparison
   */
  static getPerformanceComparison(): {
    beforeOptimization: PerformanceStats | null;
    afterOptimization: PerformanceStats | null;
    improvement: {
      responseTimeImprovement: number;
      apiCallReduction: number;
      dataTransferReduction: number;
      serverSideFilteringAdoption: number;
    };
  } {
    const metrics = this.getMetrics();
    const now = Date.now();
    const oneHourAgo = now - 60 * 60 * 1000;

    // Split metrics: before and after the last hour (as a simple before/after)
    const beforeMetrics = metrics.filter(m => m.timestamp < oneHourAgo);
    const afterMetrics = metrics.filter(m => m.timestamp >= oneHourAgo);

    const calculateStats = (
      metricsList: SearchMetric[]
    ): PerformanceStats | null => {
      if (metricsList.length === 0) return null;

      const totalResponseTime = metricsList.reduce(
        (sum, m) => sum + m.responseTime,
        0
      );
      const totalApiCalls = metricsList.reduce((sum, m) => sum + m.apiCalls, 0);
      const totalDataTransferred = metricsList.reduce(
        (sum, m) => sum + m.dataTransferred,
        0
      );
      const clientSideCount = metricsList.filter(
        m => m.clientSideFiltering
      ).length;
      const serverSideCount = metricsList.filter(
        m => m.serverSideFiltering
      ).length;

      return {
        totalSearches: metricsList.length,
        averageResponseTime: totalResponseTime / metricsList.length,
        totalApiCalls,
        totalDataTransferred,
        cacheHitRate: 0, // Not calculated here
        clientSideFilteringCount: clientSideCount,
        serverSideFilteringCount: serverSideCount,
        lastUpdated: now,
      };
    };

    const beforeStats = calculateStats(beforeMetrics);
    const afterStats = calculateStats(afterMetrics);

    const improvement = {
      responseTimeImprovement:
        beforeStats && afterStats
          ? ((beforeStats.averageResponseTime -
              afterStats.averageResponseTime) /
              beforeStats.averageResponseTime) *
            100
          : 0,
      apiCallReduction:
        beforeStats && afterStats
          ? ((beforeStats.totalApiCalls - afterStats.totalApiCalls) /
              beforeStats.totalApiCalls) *
            100
          : 0,
      dataTransferReduction:
        beforeStats && afterStats
          ? ((beforeStats.totalDataTransferred -
              afterStats.totalDataTransferred) /
              beforeStats.totalDataTransferred) *
            100
          : 0,
      serverSideFilteringAdoption: afterStats
        ? (afterStats.serverSideFilteringCount / afterStats.totalSearches) * 100
        : 0,
    };

    return {
      beforeOptimization: beforeStats,
      afterOptimization: afterStats,
      improvement,
    };
  }

  /**
   * Clear all performance data
   */
  static clearData(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.STATS_KEY);
      console.log('📊 [PERFORMANCE] All performance data cleared');
    } catch (error) {
      console.warn('Failed to clear performance data:', error);
    }
  }

  /**
   * Export performance data for analysis
   */
  static exportData(): string {
    const metrics = this.getMetrics();
    const stats = this.getStats();
    const comparison = this.getPerformanceComparison();

    return JSON.stringify(
      {
        exportDate: new Date().toISOString(),
        metrics,
        stats,
        comparison,
      },
      null,
      2
    );
  }

  /**
   * Start monitoring a search operation
   */
  static startTimer(): {
    stop: (metric: Omit<SearchMetric, 'timestamp' | 'responseTime'>) => void;
  } {
    const startTime = Date.now();

    return {
      stop: (metric: Omit<SearchMetric, 'timestamp' | 'responseTime'>) => {
        const responseTime = Date.now() - startTime;
        this.recordSearch({
          ...metric,
          responseTime,
        });
      },
    };
  }

  /**
   * Get recent performance trends
   */
  static getTrends(hoursBack: number = 24): {
    hourly: Array<{
      hour: string;
      searches: number;
      avgResponseTime: number;
      apiCalls: number;
      serverSideFiltering: number;
    }>;
  } {
    const metrics = this.getMetrics();
    const now = Date.now();
    const cutoff = now - hoursBack * 60 * 60 * 1000;

    const recentMetrics = metrics.filter(m => m.timestamp >= cutoff);

    // Group by hour
    const hourlyData = new Map<string, SearchMetric[]>();

    recentMetrics.forEach(metric => {
      const hour = new Date(metric.timestamp).toISOString().substring(0, 13); // YYYY-MM-DDTHH
      if (!hourlyData.has(hour)) {
        hourlyData.set(hour, []);
      }
      hourlyData.get(hour)!.push(metric);
    });

    const hourly = Array.from(hourlyData.entries()).map(
      ([hour, hourMetrics]) => ({
        hour,
        searches: hourMetrics.length,
        avgResponseTime:
          hourMetrics.reduce((sum, m) => sum + m.responseTime, 0) /
          hourMetrics.length,
        apiCalls: hourMetrics.reduce((sum, m) => sum + m.apiCalls, 0),
        serverSideFiltering: hourMetrics.filter(m => m.serverSideFiltering)
          .length,
      })
    );

    return { hourly };
  }
}

export default PerformanceMonitorService;
